import apiClient from "../lib/api-client";

function mutationKey(storageKey: string) {
  const existing = sessionStorage.getItem(storageKey);
  if (existing) return existing;
  const key = crypto.randomUUID();
  sessionStorage.setItem(storageKey, key);
  return key;
}

export type PurchaseReturnStatus =
  | "DRAFT"
  | "SUBMITTED"
  | "APPROVED"
  | "RESERVED"
  | "DISPATCHED"
  | "ACKNOWLEDGED"
  | "COMPLETED"
  | "CANCELLED";

export type PurchaseReturnSupplierOutcome =
  | "FULL_CREDIT"
  | "PARTIAL_CREDIT"
  | "REJECTED"
  | "REPLACEMENT"
  | "REPAIR"
  | "RETURN_TO_PHARMACY";

export interface TransitionPurchaseReturnDto {
  toStatus: Exclude<PurchaseReturnStatus, "DRAFT">;
  reason: string;
  approvalRequestId?: string;
  reference?: string;
  supplierOutcome?: PurchaseReturnSupplierOutcome;
  outcomeAmountPaise?: number;
}

export type ReturnReason =
  | "DAMAGED"
  | "DEFECTIVE"
  | "EXPIRED"
  | "WRONG_ITEM"
  | "QUALITY_ISSUE"
  | "OVERSTOCK"
  | "RECALL"
  | "OTHER";

export interface PurchaseReturnItem {
  id: string;
  product: { id: string; name: string; sku?: string };
  batch: { id: string; batchNumber: string };
  qtyReturned: number;
  unitPrice: number;
  taxPercent: number;
  taxAmount: number;
  totalAmount: number;
  reason?: string;
}

export interface PurchaseReturn {
  id: string;
  returnNumber: string;
  returnDate: string;
  status: PurchaseReturnStatus;
  returnReason: ReturnReason;
  supplier: { id: string; name: string; phone?: string };
  grnId?: string | null;
  goodsReceipt?: { id: string; grnNumber: string; grnDate?: string } | null;
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  notes?: string | null;
  approvalRequestId?: string | null;
  items: PurchaseReturnItem[];
}

export interface CreatePurchaseReturnDto {
  returnReason: ReturnReason;
  notes?: string;
  items: Array<{
    goodsReceiptItemId?: string;
    productId?: string;
    batchId?: string;
    lotId: string;
    quantity: string;
    originalBucket?: "available" | "quarantine" | "damaged";
    reason?: string;
  }>;
}

export const purchaseReturnService = {
  async getPurchaseReturns(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: PurchaseReturnStatus;
    supplierId?: string;
    storeId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{
    data: PurchaseReturn[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const response = await apiClient.get("/purchase-returns", { params });
    return response.data;
  },

  async getPurchaseReturn(id: string): Promise<PurchaseReturn> {
    const response = await apiClient.get(`/purchase-returns/${id}`);
    return response.data;
  },

  async createPurchaseReturn(
    grnId: string,
    data: CreatePurchaseReturnDto,
  ): Promise<PurchaseReturn> {
    const storageKey = `medicorepay:purchase-return:create:${grnId}`;
    const response = await apiClient.post(
      `/product-revival/goods-receipts/${grnId}/returns`,
      data,
      { headers: { "Idempotency-Key": mutationKey(storageKey) } },
    );
    sessionStorage.removeItem(storageKey);
    return response.data;
  },

  async transitionPurchaseReturn(
    id: string,
    data: TransitionPurchaseReturnDto,
  ): Promise<{
    id: string;
    status: PurchaseReturnStatus;
    approvalRequestId?: string | null;
  }> {
    const storageKey = `medicorepay:purchase-return:transition:${id}:${data.toStatus}`;
    const response = await apiClient.post(
      `/product-revival/purchase-returns/${id}/transitions`,
      data,
      { headers: { "Idempotency-Key": mutationKey(storageKey) } },
    );
    sessionStorage.removeItem(storageKey);
    return response.data;
  },
};

export const PURCHASE_RETURN_STATUS_LABELS: Record<
  PurchaseReturnStatus,
  string
> = {
  DRAFT: "Draft",
  SUBMITTED: "Submitted",
  APPROVED: "Approved",
  RESERVED: "Stock Reserved",
  DISPATCHED: "Dispatched",
  ACKNOWLEDGED: "Supplier Acknowledged",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
};

export const PURCHASE_RETURN_STATUS_COLORS: Record<
  PurchaseReturnStatus,
  string
> = {
  DRAFT: "bg-yellow-100 text-yellow-800",
  SUBMITTED: "bg-cyan-100 text-cyan-800",
  APPROVED: "bg-green-100 text-green-800",
  RESERVED: "bg-indigo-100 text-indigo-800",
  DISPATCHED: "bg-blue-100 text-blue-800",
  ACKNOWLEDGED: "bg-purple-100 text-purple-800",
  COMPLETED: "bg-emerald-100 text-emerald-800",
  CANCELLED: "bg-red-100 text-red-800",
};
